import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { StaticImageData } from 'next/image';
import classnames from 'classnames';
import { A11y, Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './CarouselBenefits.module.scss';

import BaseImage from '@/components/BaseImage/BaseImage';
import BaseLink from '@/components/BaseLink/BaseLink';
import ProgressNavigationArrow from '@/components/ProgressNavigationArrow/ProgressNavigationArrow';

import useLayout from '@/hooks/use-layout';

import Arrow from '@/components/svgs/Arrow.svg';

export type Props = {
  className?: string;
  title: string;
  eyebrow: string;
  card: {
    id: number;
    image: {
      data: StaticImageData;
      alt: string;
    };
    heading: string;
    description: string;
    href: string;
  }[];
  ctaText: string;
};

function CarouselBenefits({ className, title, eyebrow, card, ctaText }: Props) {
  const { layout } = useLayout();
  const swiperRef = useRef<SwiperClass | null>(null);
  const [slideCounter, setSlideCounter] = useState(1);
  const previousSlide = useRef(0);
  const navProgressPercentage = useMemo(
    () => (slideCounter / (layout.tablet ? card.length - 1 : card.length)) * 100,
    [layout, card, slideCounter]
  );

  const handleSlideChange = useCallback(() => {
    if (swiperRef.current !== null) {
      setSlideCounter(swiperRef.current.realIndex + 1);

      previousSlide.current = swiperRef.current.realIndex;
    }
  }, []);

  return (
    <section className={classnames(styles.CarouselBenefits, className)}>
      <p className={styles.eyebrow}>{eyebrow}</p>
      <h2 className={styles.title}>{title}</h2>

      <Swiper
        modules={[A11y]}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
        }}
        slidesPerView="auto"
        slidesOffsetBefore={0}
        breakpoints={{
          1150: {
            allowTouchMove: false
          }
        }}
        onSlideChange={handleSlideChange}
      >
        {card.map(({ image, heading, description, href }, index) => (
          <SwiperSlide key={index}>
            <BaseLink href={href} className={styles.benefitSlide}>
              <div className={styles.wrapper}>
                <div className={styles.imageWrapper}>
                  <BaseImage className={styles.image} data={image.data} alt={image.alt} />
                </div>
                <h3 className={styles.itemTitle}>{heading}</h3>
                <p className={styles.content}>{description}</p>
              </div>
              <div className={styles.cta}>
                <Arrow className={styles.arrow} />
                <p className={styles.learnMore}>{ctaText}</p>
              </div>
            </BaseLink>
          </SwiperSlide>
        ))}
      </Swiper>

      <ProgressNavigationArrow
        className={styles.navArrow}
        onLeftClick={() => swiperRef.current?.slidePrev()}
        onRightClick={() => swiperRef.current?.slideNext()}
        percentage={navProgressPercentage}
      />
    </section>
  );
}

export default memo(CarouselBenefits);
